import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  LogIn, LogOut, LayoutDashboard, FileText, Settings,
  Users, TrendingUp, Search, RefreshCw, Mail, Eye,
  ChevronLeft, ChevronRight, Loader2, AlertCircle
} from 'lucide-react';
import { Logo } from '../components/Logo';
import { adminApi } from '../lib/api';
import { toast, Toaster } from 'sonner';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

export default function AdminPage() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [token, setToken] = useState(localStorage.getItem('admin_token') || '');
  const [activeTab, setActiveTab] = useState('dashboard');
  
  // Login state
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);
  const [loginError, setLoginError] = useState('');

  // Dashboard state
  const [stats, setStats] = useState(null);
  const [cases, setCases] = useState([]);
  const [casesLoading, setCasesLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(0);
  const [totalCases, setTotalCases] = useState(0);
  const pageSize = 10;

  // Selected case
  const [selectedCase, setSelectedCase] = useState(null);

  // Check existing token on mount
  useEffect(() => {
    const verify = async () => {
      try {
        await adminApi.getStats(token);
        setIsAuthenticated(true);
      } catch {
        localStorage.removeItem('admin_token');
        setToken('');
        setIsAuthenticated(false);
      }
    };
    if (token) {
      verify();
    }
  }, [token]);

  const verifyToken = async () => {
    try {
      await adminApi.getStats(token);
      setIsAuthenticated(true);
    } catch {
      localStorage.removeItem('admin_token');
      setToken('');
      setIsAuthenticated(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginLoading(true);
    setLoginError('');

    try {
      const result = await adminApi.login(username, password);
      setToken(result.token);
      localStorage.setItem('admin_token', result.token);
      setIsAuthenticated(true);
    } catch (err) {
      setLoginError('Credenciais inválidas');
    } finally {
      setLoginLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    setToken('');
    setIsAuthenticated(false);
    setStats(null);
    setCases([]);
  };

  const loadDashboard = async () => {
    try {
      const statsData = await adminApi.getStats(token);
      setStats(statsData);
    } catch (err) {
      console.error('Dashboard load error:', err);
    }
  };

  const loadCases = async () => {
    setCasesLoading(true);
    try {
      const params = {
        skip: currentPage * pageSize,
        limit: pageSize
      };
      if (statusFilter !== 'all') {
        params.status = statusFilter;
      }
      const result = await adminApi.getCases(token, params);
      setCases(result.cases);
      setTotalCases(result.total);
    } catch (err) {
      console.error('Cases load error:', err);
    } finally {
      setCasesLoading(false);
    }
  };

  useEffect(() => {
    if (isAuthenticated) {
      loadCases();
      loadDashboard();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, statusFilter, isAuthenticated]);

  const handleResendEmail = async (caseId) => {
    try {
      await adminApi.resendEmail(token, caseId);
      toast.success('Email reenviado com sucesso!');
    } catch (err) {
      toast.error('Erro ao reenviar email');
    }
  };

  const viewCaseDetails = async (caseId) => {
    try {
      const caseData = await adminApi.getCase(token, caseId);
      setSelectedCase(caseData);
    } catch (err) {
      toast.error('Erro ao carregar detalhes');
    }
  };

  // Login Screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-obsidian flex items-center justify-center px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md"
        >
          <div className="text-center mb-8">
            <Logo size="default" className="justify-center" />
            <p className="text-gray-500 mt-2">Painel Administrativo</p>
          </div>

          <form onSubmit={handleLogin} className="glass-card p-8">
            <div className="w-16 h-16 rounded-full bg-gold/20 flex items-center justify-center mx-auto mb-6">
              <LogIn className="w-8 h-8 text-gold" />
            </div>

            {loginError && (
              <div className="bg-crimson/20 border border-crimson/50 rounded-lg p-3 mb-4 flex items-center gap-2">
                <AlertCircle className="w-4 h-4 text-crimson" />
                <span className="text-crimson text-sm">{loginError}</span>
              </div>
            )}

            <div className="space-y-4">
              <div>
                <label className="text-sm text-gray-400 mb-2 block">Usuário</label>
                <input
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className="w-full input-dark"
                  placeholder="admin"
                  required
                />
              </div>
              <div>
                <label className="text-sm text-gray-400 mb-2 block">Senha</label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full input-dark"
                  placeholder="••••••••"
                  required
                />
              </div>
              <button
                type="submit"
                disabled={loginLoading}
                className="w-full btn-gold flex items-center justify-center gap-2"
              >
                {loginLoading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <>
                    <LogIn className="w-4 h-4" />
                    Entrar
                  </>
                )}
              </button>
            </div>
          </form>
        </motion.div>
      </div>
    );
  }

  // Admin Dashboard
  return (
    <div className="min-h-screen bg-obsidian">
      <Toaster position="top-right" theme="dark" />
      
      {/* Header */}
      <header className="px-6 py-4 border-b border-white/5 bg-obsidian-paper sticky top-0 z-50">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Logo size="small" />
            <span className="text-gray-500 text-sm">Admin</span>
          </div>
          <button
            onClick={handleLogout}
            className="text-gray-400 hover:text-white flex items-center gap-2 text-sm transition-colors"
          >
            <LogOut className="w-4 h-4" />
            Sair
          </button>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
            <div className="glass-card p-6">
              <div className="flex items-center gap-3 mb-2">
                <FileText className="w-5 h-5 text-gold" />
                <span className="text-gray-400 text-sm">Total de Casos</span>
              </div>
              <span className="text-3xl font-heading text-white">{stats.total_cases}</span>
            </div>
            <div className="glass-card p-6">
              <div className="flex items-center gap-3 mb-2">
                <Users className="w-5 h-5 text-green-500" />
                <span className="text-gray-400 text-sm">Casos Pagos</span>
              </div>
              <span className="text-3xl font-heading text-white">{stats.paid_cases}</span>
            </div>
            <div className="glass-card p-6">
              <div className="flex items-center gap-3 mb-2">
                <FileText className="w-5 h-5 text-yellow-500" />
                <span className="text-gray-400 text-sm">Rascunhos</span>
              </div>
              <span className="text-3xl font-heading text-white">{stats.draft_cases}</span>
            </div>
            <div className="glass-card p-6">
              <div className="flex items-center gap-3 mb-2">
                <TrendingUp className="w-5 h-5 text-blue-500" />
                <span className="text-gray-400 text-sm">Taxa Conversão</span>
              </div>
              <span className="text-3xl font-heading text-white">{stats.conversion_rate}%</span>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="flex flex-col md:flex-row gap-4 mb-6">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
            <input
              type="text"
              placeholder="Buscar por email, nome..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full input-dark pl-10"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-full md:w-48 bg-white/5 border-white/10 text-white">
              <SelectValue placeholder="Filtrar por status" />
            </SelectTrigger>
            <SelectContent className="bg-obsidian-paper border-white/10">
              <SelectItem value="all" className="text-white">Todos</SelectItem>
              <SelectItem value="draft" className="text-white">Rascunho</SelectItem>
              <SelectItem value="paid" className="text-white">Pago</SelectItem>
              <SelectItem value="generated" className="text-white">Gerado</SelectItem>
              <SelectItem value="emailed" className="text-white">Enviado</SelectItem>
            </SelectContent>
          </Select>
          <button
            onClick={loadCases}
            className="btn-outline-gold flex items-center gap-2"
          >
            <RefreshCw className="w-4 h-4" />
            Atualizar
          </button>
        </div>

        {/* Cases Table */}
        <div className="glass-card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-white/10">
                  <th className="px-6 py-4 text-left text-xs text-gray-500 uppercase tracking-wider">ID</th>
                  <th className="px-6 py-4 text-left text-xs text-gray-500 uppercase tracking-wider">Nome</th>
                  <th className="px-6 py-4 text-left text-xs text-gray-500 uppercase tracking-wider">Email</th>
                  <th className="px-6 py-4 text-left text-xs text-gray-500 uppercase tracking-wider">País</th>
                  <th className="px-6 py-4 text-left text-xs text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-4 text-left text-xs text-gray-500 uppercase tracking-wider">Data</th>
                  <th className="px-6 py-4 text-left text-xs text-gray-500 uppercase tracking-wider">Ações</th>
                </tr>
              </thead>
              <tbody>
                {casesLoading ? (
                  <tr>
                    <td colSpan={7} className="px-6 py-12 text-center">
                      <Loader2 className="w-6 h-6 text-gold animate-spin mx-auto" />
                    </td>
                  </tr>
                ) : cases.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-6 py-12 text-center text-gray-500">
                      Nenhum caso encontrado
                    </td>
                  </tr>
                ) : (
                  cases
                    .filter(c => 
                      !searchQuery || 
                      c.nome?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                      c.email?.toLowerCase().includes(searchQuery.toLowerCase())
                    )
                    .map((caseItem) => (
                      <tr key={caseItem.id} className="border-b border-white/5 hover:bg-white/5">
                        <td className="px-6 py-4 font-mono text-sm text-gold">{caseItem.id}</td>
                        <td className="px-6 py-4 text-white">{caseItem.nome || 'N/A'}</td>
                        <td className="px-6 py-4 text-gray-400 text-sm">{caseItem.email}</td>
                        <td className="px-6 py-4 text-gray-400">{caseItem.country_now || 'N/A'}</td>
                        <td className="px-6 py-4">
                          <span className={`px-2 py-1 rounded text-xs uppercase ${
                            caseItem.status === 'emailed' ? 'bg-green-500/20 text-green-500' :
                            caseItem.status === 'generated' ? 'bg-blue-500/20 text-blue-500' :
                            caseItem.status === 'paid' ? 'bg-yellow-500/20 text-yellow-500' :
                            'bg-gray-500/20 text-gray-500'
                          }`}>
                            {caseItem.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-gray-500 text-sm">
                          {caseItem.created_at ? new Date(caseItem.created_at).toLocaleDateString('pt-BR') : 'N/A'}
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => viewCaseDetails(caseItem.id)}
                              className="p-2 hover:bg-white/10 rounded transition-colors"
                              title="Ver detalhes"
                            >
                              <Eye className="w-4 h-4 text-gray-400" />
                            </button>
                            {(caseItem.status === 'generated' || caseItem.status === 'emailed') && (
                              <button
                                onClick={() => handleResendEmail(caseItem.id)}
                                className="p-2 hover:bg-white/10 rounded transition-colors"
                                title="Reenviar email"
                              >
                                <Mail className="w-4 h-4 text-gray-400" />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="px-6 py-4 border-t border-white/10 flex items-center justify-between">
            <span className="text-sm text-gray-500">
              Mostrando {currentPage * pageSize + 1} - {Math.min((currentPage + 1) * pageSize, totalCases)} de {totalCases}
            </span>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setCurrentPage(Math.max(0, currentPage - 1))}
                disabled={currentPage === 0}
                className="p-2 hover:bg-white/10 rounded disabled:opacity-50 transition-colors"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <button
                onClick={() => setCurrentPage(currentPage + 1)}
                disabled={(currentPage + 1) * pageSize >= totalCases}
                className="p-2 hover:bg-white/10 rounded disabled:opacity-50 transition-colors"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>

        {/* Case Detail Modal */}
        {selectedCase && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-6">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="glass-card max-w-3xl w-full max-h-[80vh] overflow-y-auto p-6"
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="font-heading text-xl text-white">
                  Caso {selectedCase.id}
                </h2>
                <button
                  onClick={() => setSelectedCase(null)}
                  className="text-gray-400 hover:text-white"
                >
                  ✕
                </button>
              </div>

              <div className="grid grid-cols-2 gap-4 mb-6">
                <div>
                  <span className="text-gray-500 text-sm">Nome</span>
                  <p className="text-white">{selectedCase.nome}</p>
                </div>
                <div>
                  <span className="text-gray-500 text-sm">Email</span>
                  <p className="text-white">{selectedCase.email}</p>
                </div>
                <div>
                  <span className="text-gray-500 text-sm">WhatsApp</span>
                  <p className="text-white">{selectedCase.whatsapp}</p>
                </div>
                <div>
                  <span className="text-gray-500 text-sm">País</span>
                  <p className="text-white">{selectedCase.country_now}</p>
                </div>
                <div>
                  <span className="text-gray-500 text-sm">Objetivo</span>
                  <p className="text-white capitalize">{selectedCase.goal}</p>
                </div>
                <div>
                  <span className="text-gray-500 text-sm">Cupom</span>
                  <p className="text-gold font-mono">{selectedCase.coupon_code || 'N/A'}</p>
                </div>
              </div>

              {selectedCase.free_text && (
                <div className="mb-6">
                  <span className="text-gray-500 text-sm block mb-2">Descrição do Cliente</span>
                  <div className="bg-white/5 rounded-lg p-4 text-gray-300 text-sm whitespace-pre-wrap">
                    {selectedCase.free_text}
                  </div>
                </div>
              )}

              {selectedCase.preview_text && (
                <div className="mb-6">
                  <span className="text-gray-500 text-sm block mb-2">Prévia</span>
                  <div className="bg-white/5 rounded-lg p-4 text-gray-300 text-sm whitespace-pre-wrap max-h-48 overflow-y-auto">
                    {selectedCase.preview_text}
                  </div>
                </div>
              )}

              <div className="flex gap-3">
                <button
                  onClick={() => setSelectedCase(null)}
                  className="flex-1 btn-outline-gold"
                >
                  Fechar
                </button>
                {(selectedCase.status === 'generated' || selectedCase.status === 'emailed') && (
                  <button
                    onClick={() => handleResendEmail(selectedCase.id)}
                    className="flex-1 btn-gold flex items-center justify-center gap-2"
                  >
                    <Mail className="w-4 h-4" />
                    Reenviar Email
                  </button>
                )}
              </div>
            </motion.div>
          </div>
        )}
      </div>
    </div>
  );
}
